import React, { useEffect, useState } from 'react'
export type PixelHourglassProps = {
  /**
   * Size of the hourglass
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Speed of the animation
   * @default "medium"
   */
  speed?: 'slow' | 'medium' | 'fast'
  /**
   * Color of the sand
   * @default "keppel"
   */
  color?: 'elf-green' | 'keppel' | 'madang' | 'gossip' | 'orinoco'
  /**
   * Optional data-id for testing
   */
  'data-id'?: string
}
export const PixelHourglass: React.FC<PixelHourglassProps> = ({
  size = 'medium',
  speed = 'medium',
  color = 'keppel',
  'data-id': dataId,
}) => {
  // Border is always 2px
  const borderSize = 2
  // Sand size varies by size prop
  const sandSize = {
    small: 4,
    medium: 6,
    large: 10,
  }[size]
  // Sand color classes
  const sandColor = {
    'elf-green': '#077f5d',
    keppel: '#3cbe8c',
    madang: '#a9edba',
    gossip: '#cdf7bd',
    orinoco: '#dffbd0',
  }[color]
  // Animation duration in milliseconds
  const durationMs = {
    slow: 4000,
    medium: 2000,
    fast: 1000,
  }[speed]
  const duration = `${durationMs}ms`
  // State to track rotation and animation
  const [rotation, setRotation] = useState(0)
  const [isRotating, setIsRotating] = useState(false)
  const [isInverted, setIsInverted] = useState(false)
  const [animationKey, setAnimationKey] = useState(0)
  useEffect(() => {
    // When bottom is full (at exactly durationMs), start rotation
    const rotateTimer = setTimeout(() => {
      setIsRotating(true)
      setRotation((prev) => prev - 180)
      setIsInverted((prev) => !prev) // Toggle inverted state
    }, durationMs)
    // After rotation completes, restart sand animations
    const resetTimer = setTimeout(() => {
      setIsRotating(false)
      setAnimationKey((prev) => prev + 1) // Force restart animations
    }, durationMs + 600) // 600ms for rotation animation
    return () => {
      clearTimeout(rotateTimer)
      clearTimeout(resetTimer)
    }
  }, [durationMs, animationKey])
  const Pixel = ({
    bg,
    animated,
  }: {
    bg: string
    animated?: 'top' | 'bottom' | 'fall'
  }) => {
    const pixelSize = bg === 'frame' ? borderSize : sandSize
    // Swap animations when inverted
    let actualAnimation = animated
    if (isInverted && animated === 'top') {
      actualAnimation = 'bottom'
    } else if (isInverted && animated === 'bottom') {
      actualAnimation = 'top'
    }
    return (
      <div
        key={animationKey}
        style={{
          width: `${pixelSize}px`,
          height: `${pixelSize}px`,
          backgroundColor:
            bg === 'sand'
              ? sandColor
              : bg === 'frame'
                ? '#1a1a1a'
                : 'transparent',
          animationDuration: duration,
          animationPlayState: isRotating ? 'paused' : 'running',
        }}
        className={
          actualAnimation === 'top'
            ? 'sand-top'
            : actualAnimation === 'bottom'
              ? 'sand-bottom'
              : actualAnimation === 'fall'
                ? 'sand-fall'
                : ''
        }
      />
    )
  }
  return (
    <div
      className="inline-flex flex-col items-center"
      data-id={dataId}
      style={{
        filter: 'drop-shadow(2px 2px 0px #1a1a1a)',
        gap: 0,
        transform: `rotate(${rotation}deg)`,
        transition: isRotating
          ? 'transform 600ms cubic-bezier(0.4, 0.0, 0.2, 1)'
          : 'none',
      }}
    >
      {/* Top section of hourglass */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        {/* Top row - widest */}
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
        </div>
        {/* Top sand area */}
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="transparent" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="transparent" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="transparent" />
          <Pixel bg="transparent" />
          <Pixel bg="sand" animated="top" />
          <Pixel bg="transparent" />
          <Pixel bg="transparent" />
          <Pixel bg="frame" />
        </div>
      </div>
      {/* Middle section - narrow neck */}
      <div
        style={{
          display: 'flex',
          gap: 0,
        }}
      >
        <Pixel bg="frame" />
        <Pixel bg="transparent" />
        <Pixel bg="transparent" />
        <Pixel bg="sand" animated="fall" />
        <Pixel bg="transparent" />
        <Pixel bg="transparent" />
        <Pixel bg="frame" />
      </div>
      {/* Bottom section of hourglass */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="transparent" />
          <Pixel bg="transparent" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="transparent" />
          <Pixel bg="transparent" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="transparent" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="transparent" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="frame" />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="sand" animated="bottom" />
          <Pixel bg="frame" />
        </div>
        {/* Bottom row */}
        <div
          style={{
            display: 'flex',
            gap: 0,
          }}
        >
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
          <Pixel bg="frame" />
        </div>
      </div>
    </div>
  )
}
